import { formatInTimeZone } from 'date-fns-tz';

import { shiftDateKey } from '@apps/web/lib/date_utils';

// Spend rollups use UTC calendar days. Keeping display and aggregation in the
// same timezone avoids moving calls across bucket boundaries in the browser.
export const LLM_SPEND_TIMEZONE = 'UTC';

// The merged priced ledger begins on this UTC day; the API rejects earlier
// starts.
export const LLM_SPEND_EARLIEST_DATE = '2026-05-22';
export const LLM_SPEND_MAX_RANGE_DAYS = 200;

export const SPEND_MODE_VALUES = ['live', 'sandbox', 'combined'] as const;
export type SpendMode = (typeof SPEND_MODE_VALUES)[number];

export type SpendBucketSize = 'day' | 'week';

// Which agent generation the outreach and scheduler tabs describe. 'all'
// keeps V2 and V3 together and draws them as separate lines.
export const AGENT_GENERATION_VALUES = ['all', 'v2', 'v3'] as const;
export type AgentGenerationSelection = (typeof AGENT_GENERATION_VALUES)[number];
export const AGENT_GENERATION_OPTIONS: ReadonlyArray<{
    readonly value: AgentGenerationSelection;
    readonly label: string;
}> = [
    { value: 'all', label: 'V2 and V3' },
    { value: 'v2', label: 'V2 only' },
    { value: 'v3', label: 'V3 only' },
];

// One entry in a stacked or multi-line chart. `color` is a CSS custom
// property from llm_spend.css so light and dark resolve in one place.
export type SpendSeries = {
    readonly key: string;
    readonly label: string;
    readonly color: string;
};

export type ObjectiveDefinition = {
    readonly key: string;
    readonly label: string;
};

function seriesColor(slot: number): string {
    return `var(--spend-series-${slot})`;
}

const OTHER_COLOR = 'var(--spend-series-other)';

// Fixed order and color per product surface.
export const SURFACE_SERIES: ReadonlyArray<SpendSeries> = [
    { key: 'outreach_agent', label: 'Outreach agent', color: seriesColor(1) },
    { key: 'scheduler_chat', label: 'Scheduler chat', color: seriesColor(2) },
    { key: 'sms_engine', label: 'SMS engine', color: seriesColor(3) },
    {
        key: 'inline_reviewer',
        label: 'Inline reviewer',
        color: seriesColor(4),
    },
    { key: 'llm_judge', label: 'LLM judge', color: seriesColor(5) },
    { key: 'voice_qa', label: 'Voice QA', color: seriesColor(6) },
    { key: 'call_analysis', label: 'Call analysis', color: seriesColor(7) },
    { key: 'realtime_voice', label: 'Realtime voice', color: seriesColor(8) },
    { key: 'other', label: 'Other', color: OTHER_COLOR },
];

// Keep the token-price components in a stable stack order.
export const COMPONENT_SERIES: ReadonlyArray<SpendSeries> = [
    { key: 'cache_write', label: 'Cache write', color: seriesColor(1) },
    { key: 'cache_read', label: 'Cache read', color: seriesColor(2) },
    { key: 'input', label: 'Uncached input', color: seriesColor(3) },
    { key: 'output', label: 'Output', color: seriesColor(4) },
];

// V2 and V3 keep the same two colors on every comparison chart.
export const GENERATION_SERIES: ReadonlyArray<SpendSeries> = [
    { key: 'v2', label: 'V2', color: seriesColor(1) },
    { key: 'v3', label: 'V3', color: seriesColor(2) },
];

// The lines a per-generation chart draws: both when comparing, otherwise
// only the selected generation.
export function generationSeriesFor(
    selection: AgentGenerationSelection,
): ReadonlyArray<SpendSeries> {
    return selection === 'all'
        ? GENERATION_SERIES
        : GENERATION_SERIES.filter((series) => series.key === selection);
}

// Wake objectives the rollup classifier can emit. Only objectives with spend
// in the selected range are rendered.
export const OBJECTIVE_SERIES: ReadonlyArray<ObjectiveDefinition> = [
    { key: 'kickoff', label: 'Kickoff' },
    { key: 'reply_handling', label: 'Reply handling' },
    { key: 'retry_round', label: 'Retry round' },
    { key: 'add_caregivers', label: 'Add caregivers' },
    { key: 'auto_start', label: 'Auto start' },
    { key: 'monitor_check', label: 'Monitor check' },
    {
        key: 'recommendation_rerun',
        label: 'Recommendation rerun',
    },
    { key: 'fill_shift', label: 'Fill shift' },
    {
        key: 'remove_caregivers',
        label: 'Remove caregivers',
    },
    {
        key: 'start_stop',
        label: 'Start or stop outreach',
    },
    { key: 'save_memory', label: 'Save memory' },
    { key: 'trigger_ehr_sync', label: 'EHR sync' },
    {
        key: 'coordinator_message_with_action',
        label: 'Other coordinator tool action',
    },
    {
        key: 'answer_question',
        label: 'Answer question',
    },
    {
        key: 'approval_resume',
        label: 'Resume after approval',
    },
    {
        key: 'scheduled_start',
        label: 'Scheduled start',
    },
    { key: 'automation', label: 'Automation' },
    { key: 'slack_thread', label: 'Slack thread' },
];
export const OBJECTIVE_OTHER_SERIES: ObjectiveDefinition = {
    key: 'other',
    label: 'Other',
};

// Scheduler chat can perform most coordinator actions. Only objectives with
// spend in the selected range are rendered; unexpected classifier values fold
// into Other instead of creating an unbounded legend.
export const SCHEDULER_OBJECTIVE_SERIES: ReadonlyArray<ObjectiveDefinition> = [
    { key: 'fill_shift', label: 'Fill shift' },
    { key: 'assign_shift', label: 'Assign shift' },
    { key: 'create_shift', label: 'Create shift' },
    { key: 'update_shift', label: 'Update shift' },
    { key: 'cancel_shift', label: 'Cancel shift' },
    { key: 'delete_shift', label: 'Delete shift' },
    { key: 'clock_shift', label: 'Clock shift' },
    { key: 'draft_outreach', label: 'Draft outreach' },
    { key: 'add_caregivers', label: 'Add caregivers' },
    {
        key: 'remove_caregivers',
        label: 'Remove caregivers',
    },
    {
        key: 'start_stop',
        label: 'Start or stop outreach',
    },
    {
        key: 'recommendation_rerun',
        label: 'Rerun recommendations',
    },
    {
        key: 'caregiver_recommendations',
        label: 'Recommend caregivers',
    },
    {
        key: 'review_caregivers',
        label: 'Review caregivers',
    },
    { key: 'review_clients', label: 'Review clients' },
    { key: 'review_shifts', label: 'Review shifts' },
    {
        key: 'review_outreach',
        label: 'Review outreach',
    },
    {
        key: 'update_caregiver',
        label: 'Update caregiver',
    },
    {
        key: 'caregiver_availability',
        label: 'Caregiver availability',
    },
    {
        key: 'contact_caregiver',
        label: 'Contact caregiver',
    },
    { key: 'update_client', label: 'Update client' },
    {
        key: 'manage_care_coordinators',
        label: 'Manage care coordinators',
    },
    { key: 'save_memory', label: 'Save memory' },
    { key: 'trigger_ehr_sync', label: 'EHR sync' },
    { key: 'ehr_note', label: 'Add EHR note' },
    {
        key: 'scheduling_rules',
        label: 'Scheduling rules',
    },
    { key: 'reporting', label: 'Reporting' },
    { key: 'watch_changes', label: 'Watch changes' },
    {
        key: 'answer_question',
        label: 'Answer question',
    },
    {
        key: 'approval_resume',
        label: 'Resume after approval',
    },
    {
        key: 'coordinator_message_with_action',
        label: 'Other coordinator tool action',
    },
    { key: 'automation', label: 'Automation' },
];

export const RANGE_PRESET_VALUES = [
    'trailing_7d',
    'trailing_30d',
    'trailing_90d',
    'all',
    'custom',
] as const;
export type RangePreset = (typeof RANGE_PRESET_VALUES)[number];

export const RANGE_PRESET_OPTIONS: ReadonlyArray<{
    readonly value: RangePreset;
    readonly label: string;
    // Compact phrase for delta chips ("vs prior 4w").
    readonly short: string;
    readonly days: number | null;
}> = [
    { value: 'trailing_7d', label: 'Last 7 days', short: '7 days', days: 7 },
    {
        value: 'trailing_30d',
        label: 'Last 30 days',
        short: '30 days',
        days: 30,
    },
    {
        value: 'trailing_90d',
        label: 'Last 90 days',
        short: '90 days',
        days: 90,
    },
    {
        value: 'all',
        label: 'All available data',
        short: 'period',
        days: null,
    },
    { value: 'custom', label: 'Custom range', short: 'period', days: null },
];

export const DEFAULT_RANGE_PRESET: RangePreset = 'trailing_30d';

export type SpendDateRange = {
    // Inclusive UTC date, YYYY-MM-DD.
    readonly start: string;
    // Exclusive UTC date, YYYY-MM-DD.
    readonly end: string;
};

export type InclusiveSpendDateRange = {
    readonly from: string;
    readonly to: string;
};

// The rollup covers complete days, so the range ends at today's UTC date
// (exclusive) and the last plotted day is yesterday.
export function presetDateRange(
    preset: RangePreset,
    now: Date = new Date(),
    customRange?: InclusiveSpendDateRange,
): SpendDateRange {
    const end = formatInTimeZone(now, LLM_SPEND_TIMEZONE, 'yyyy-MM-dd');
    const option = RANGE_PRESET_OPTIONS.find((row) => row.value === preset);
    if (preset === 'custom' && customRange !== undefined) {
        return {
            start: customRange.from,
            end: shiftDateKey(customRange.to, 1),
        };
    }
    if (preset === 'all') {
        return { start: LLM_SPEND_EARLIEST_DATE, end };
    }
    if (option === undefined || option.days === null) {
        return {
            start: shiftDateKey(end, -30),
            end,
        };
    }
    return { start: shiftDateKey(end, -option.days), end };
}

export function rangePresetShortLabel(preset: RangePreset): string {
    return (
        RANGE_PRESET_OPTIONS.find((row) => row.value === preset)?.short ??
        preset
    );
}
